/**
 * EntityTypeEditForm — inline edit form for "create custom entity type"
 * actions.
 *
 * Mirrors PropertyTypeEditForm's pattern: maps between the action
 * payload's CRM property keys (ahoy_display_name, ahoy_display_name_plural,
 * ahoy_description, ahoy_icon) and a local form state, and registers
 * save/cancel handlers into ActionEditContext so the parent
 * ActionAccordion drives the footer buttons.
 *
 * Reuses the IconSelect component from the CRM icon system (the same
 * picker used by CreateObjectTypeWizard and EditEntityTypeSlideout).
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { HintText } from '@/lib/base/forms/hint-text';
import { Input } from '@/lib/base/forms/input/input';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { IconSelect } from '@/lib/crm/utils/icons/IconSelect';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { useActionEdit } from './ActionAccordion';
import { extractProperties, wrapProperties } from './CreateActionCard.helpers';
import { useActionPayloadSave } from './shared';
import { SaveValidationError } from './shared/SaveValidationError';

// ─── Form value type ────────────────────────────────────────────────────────

interface EntityTypeFormValues {
  displayName: string;
  displayNamePlural: string;
  description: string;
  icon: string;
}

// ─── Payload ↔ form value conversion ────────────────────────────────────────

function payloadToFormValues(
  flat: Record<string, unknown>
): EntityTypeFormValues {
  return {
    displayName:
      typeof flat.ahoy_display_name === 'string' ? flat.ahoy_display_name : '',
    displayNamePlural:
      typeof flat.ahoy_display_name_plural === 'string'
        ? flat.ahoy_display_name_plural
        : '',
    description:
      typeof flat.ahoy_description === 'string' ? flat.ahoy_description : '',
    icon: typeof flat.ahoy_icon === 'string' ? flat.ahoy_icon : '',
  };
}

function formValuesToPayload(
  values: EntityTypeFormValues,
  originalFlat: Record<string, unknown>
): Record<string, unknown> {
  // Start from the original payload so we preserve fields not shown in
  // the form (ahoy_type_name, ahoy_allows_custom_properties, etc.)
  const result = { ...originalFlat };
  result.ahoy_display_name = values.displayName;
  result.ahoy_display_name_plural = values.displayNamePlural || undefined;
  result.ahoy_description = values.description || undefined;
  result.ahoy_icon = values.icon || undefined;
  return result;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface EntityTypeEditFormProps {
  action: ProposedAction<ObjectCreatedEntityPayload>;
  proposalId?: number;
  orgId?: number;
  onUpdate?: ActionRendererProps['onUpdate'];
}

export function EntityTypeEditForm({
  action,
  proposalId,
  orgId,
  onUpdate,
}: EntityTypeEditFormProps) {
  const { setDirty, saveHandlerRef, cancelHandlerRef } = useActionEdit();
  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });

  const originalFlat = useMemo(
    () => extractProperties(action.payload.payload.properties),
    [action.payload.payload.properties]
  );

  const initialValues = useMemo(
    () => payloadToFormValues(originalFlat),
    [originalFlat]
  );

  const [values, setValues] = useState<EntityTypeFormValues>(initialValues);

  // Becomes true once a save is attempted with invalid input, which reveals
  // the inline field errors. Errors are derived from `values`, so they clear
  // live as the user fills in the required fields.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Sync when source payload changes (e.g. after save)
  useEffect(() => {
    setValues(payloadToFormValues(originalFlat));
  }, [originalFlat]);

  // Required-field validation. Mirrors the canonical entity-type creation flow
  // (CreateObjectTypeWizard), where display name, plural name, and icon are all
  // required; description is optional.
  const errors = useMemo<Partial<Record<keyof EntityTypeFormValues, string>>>(
    () => ({
      displayName: values.displayName.trim()
        ? undefined
        : 'Display name is required',
      displayNamePlural: values.displayNamePlural.trim()
        ? undefined
        : 'Plural name is required',
      icon: values.icon ? undefined : 'Icon is required',
    }),
    [values]
  );

  const hasErrors = useMemo(
    () => Object.values(errors).some(Boolean),
    [errors]
  );

  // Dirty state
  const isDirty = useMemo(() => {
    return JSON.stringify(values) !== JSON.stringify(initialValues);
  }, [values, initialValues]);

  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  // Save handler — updates the action payload via the proposal API
  const save = useCallback(async () => {
    if (hasErrors) {
      // Reveal inline field errors and abort the save. Throwing a
      // SaveValidationError keeps the editor open and signals the shared
      // save handler to skip the generic failure toast — the inline field
      // errors are the actionable feedback here.
      setSubmitAttempted(true);
      throw new SaveValidationError();
    }
    const updatedFlat = formValuesToPayload(values, originalFlat);
    const updatedProperties = wrapProperties(updatedFlat);
    const updatedPayload: ObjectCreatedEntityPayload = {
      ...action.payload,
      payload: {
        ...action.payload.payload,
        properties: updatedProperties,
      },
    };
    await savePayload(updatedPayload);
  }, [hasErrors, values, originalFlat, action, savePayload]);

  // Cancel handler — resets to original values
  const cancel = useCallback(() => {
    setValues(initialValues);
    setSubmitAttempted(false);
  }, [initialValues]);

  useLayoutEffect(() => {
    saveHandlerRef.current = save;
    cancelHandlerRef.current = cancel;
  });

  // Field change handlers
  const handleDisplayNameChange = useCallback(
    (v: string) => setValues(prev => ({ ...prev, displayName: v })),
    []
  );

  const handlePluralNameChange = useCallback(
    (v: string) =>
      setValues(prev => ({
        ...prev,
        displayNamePlural: v,
      })),
    []
  );

  const handleDescriptionChange = useCallback(
    (v: string) => setValues(prev => ({ ...prev, description: v })),
    []
  );

  const handleIconChange = useCallback(
    (iconName: string) => setValues(prev => ({ ...prev, icon: iconName })),
    []
  );

  return (
    <div className='px-4 py-3 space-y-4'>
      {/* Display Name */}
      <Input
        label='Display Name'
        placeholder='e.g., Ticket'
        value={values.displayName}
        onChange={handleDisplayNameChange}
        isInvalid={submitAttempted && !!errors.displayName}
        hint={submitAttempted ? errors.displayName : undefined}
      />

      {/* Plural Name */}
      <Input
        label='Plural Name'
        placeholder='e.g., Tickets'
        value={values.displayNamePlural}
        onChange={handlePluralNameChange}
        isInvalid={submitAttempted && !!errors.displayNamePlural}
        hint={submitAttempted ? errors.displayNamePlural : undefined}
      />

      {/* Description */}
      <TextArea
        label='Description'
        placeholder='Describe this object type'
        value={values.description}
        onChange={handleDescriptionChange}
        rows={2}
      />

      {/* Icon */}
      <div>
        <IconSelect
          label='Icon'
          value={values.icon || null}
          onChange={handleIconChange}
          placeholder='Select an icon'
        />
        {submitAttempted && errors.icon && (
          <HintText isInvalid className='mt-1.5'>
            {errors.icon}
          </HintText>
        )}
      </div>
    </div>
  );
}
